import { AHText, AHTextFonts, Box, useScaleFactor, useSpaceSize } from "arkh";
import { DateTime } from "luxon";
import React, { PropsWithChildren, useMemo, useRef } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import { graphql, useFragment } from "react-relay";

import { EmployeeScheduleShiftBar } from "./EmployeeScheduleShiftBar";

import { EmployeeScheduleShiftsEmployeeNamePanel_employee$key } from "./__generated__/EmployeeScheduleShiftsEmployeeNamePanel_employee.graphql";
import { EmployeeScheduleShiftsEmployee_employee$key } from "./__generated__/EmployeeScheduleShiftsEmployee_employee.graphql";

export const Employee = React.memo(EmployeeCmp);
function EmployeeCmp(props: {
  employee: EmployeeScheduleShiftsEmployee_employee$key;
  isDoubleHeight: boolean;
  listItemHeight: number;
  employeePaneWidth: number;
  dayStartMinutes: number;
  boxWidth: number;

  showTime: boolean;
}) {
  const employee = useFragment(
    graphql`
      fragment EmployeeScheduleShiftsEmployee_employee on Employee {
        id
        ...EmployeeScheduleShiftsEmployeeNamePanel_employee
        shifts {
          id
          ...EmployeeScheduleShiftBar
        }
      }
    `,
    props.employee
  );

  return (
    <View
      style={{
        height: props.listItemHeight,
        justifyContent: "center",
        width: "100%",
      }}
    >
      <NamePanel
        employeePaneWidth={props.employeePaneWidth}
        isDoubleHeight={props.isDoubleHeight}
        employee={employee}
      />

      <ShiftCanvas
        employeePaneWidth={props.employeePaneWidth}
        isDoubleHeight={props.isDoubleHeight}
        hide={!props.showTime}
      >
        {employee.shifts.map((shift, i) => (
          <EmployeeScheduleShiftBar
            key={shift.id}
            shift={shift}
            boxWidth={props.boxWidth}
            isDoubleHeight={props.isDoubleHeight}
            showTime
            dayStartMinutes={props.dayStartMinutes}
          />
        ))}
      </ShiftCanvas>
      <ShiftCanvas
        employeePaneWidth={props.employeePaneWidth}
        isDoubleHeight={props.isDoubleHeight}
        hide={props.showTime}
      >
        {employee.shifts.map((shift) => (
          <EmployeeScheduleShiftBar
            key={shift.id + "TEAM"}
            shift={shift}
            boxWidth={props.boxWidth}
            isDoubleHeight={props.isDoubleHeight}
            showTime={false}
            dayStartMinutes={props.dayStartMinutes}
          />
        ))}
      </ShiftCanvas>
    </View>
  );
}

function ShiftCanvas(
  props: PropsWithChildren<{
    employeePaneWidth: number;
    isDoubleHeight: boolean;
    hide: boolean;
  }>
) {
  const sf = useScaleFactor();
  const spaceLeft = useSpaceSize("xsmall");
  const style: StyleProp<ViewStyle> = useMemo(
    () => ({
      overflow: "hidden",
      height: props.isDoubleHeight ? 30 * sf : 17 * sf,
      justifyContent: "center",
      left: props.employeePaneWidth,
      paddingLeft: spaceLeft,
      width: "100%",
      position: "absolute",
      opacity: props.hide ? 0 : 1,
    }),
    [props.employeePaneWidth, props.hide, props.isDoubleHeight, sf, spaceLeft]
  );
  return <View style={style}>{props.children}</View>;
}

const NamePanel = React.memo(NamePanelCmp);
function NamePanelCmp(props: {
  isDoubleHeight: boolean;
  employeePaneWidth: number;
  employee: EmployeeScheduleShiftsEmployeeNamePanel_employee$key;
}) {
  const sf = useScaleFactor();
  const employee = useFragment(
    graphql`
      fragment EmployeeScheduleShiftsEmployeeNamePanel_employee on Employee {
        firstName
        lastName
      }
    `,
    props.employee
  );
  return (
    <Box
      style={{
        justifyContent: "center",
        width: props.employeePaneWidth,
      }}
    >
      <AHText
        font={AHTextFonts.BOLD}
        numberOfLines={props.isDoubleHeight ? 2 : 1}
        spaceLeft="xsmall"
        style={{ fontSize: Math.round(12 * sf) }}
        type="detail"
        ellipsizeMode="tail"
        color="darkerText"
      >
        {props.isDoubleHeight ? (
          employee.firstName + " \n" + (employee.lastName ?? "")
        ) : (
          <>
            {employee.firstName}{" "}
            {employee.lastName && employee.lastName.length > 1
              ? employee.lastName[0]
              : ""}
          </>
        )}
      </AHText>
    </Box>
  );
}
